<template>
	<div>
		<h6 class="module-title">{{ lang.nd_calendar_dashboard }}</h6>

		<v-card flat class="section-card mb-3">
			<b-row class="align-items-end">
				<b-col cols="12" md="5" lg="4">
					<v-select
						v-model="selectedCalendarType"
						:items="calendarTypeOptions"
						item-text="text"
						item-value="value"
						outlined
						dense
						hide-details
						:label="lang.nd_calendar_type"
					/>
				</b-col>
				<b-col cols="12" md="7" lg="8" class="pt-2 pt-md-0">
					<div class="summary-chips-wrap">
						<v-chip x-small dark color="primary" class="mr-2 mb-1">
							{{ lang.nd_under_process }}: {{ statusCounts.inProcess }}
						</v-chip>
						<v-chip x-small dark color="error" class="mr-2 mb-1">
							{{ lang.nd_late }}: {{ statusCounts.late }}
						</v-chip>
						<v-chip x-small dark color="success" class="mb-1">
							{{ lang.nd_closed }}: {{ statusCounts.closed }}
						</v-chip>
					</div>
				</b-col>
			</b-row>
		</v-card>

		<div v-if="loading" class="mb-3">
			<v-skeleton-loader type="table, list-item-three-line@4" />
		</div>

		<template v-else>
			<v-alert
				v-if="!activeCalendarItems.length"
				dense
				outlined
				type="info"
			>
				{{ lang.nd_no_records }}
			</v-alert>

			<div>
				<div class="calendar-toolbar">
					<v-btn icon small @click="goPrevMonth">
						<v-icon small>mdi-chevron-left</v-icon>
					</v-btn>
					<div class="calendar-title">{{ calendarMonthLabel }}</div>
					<div class="d-flex align-items-center">
						<v-btn text x-small color="primary" class="mr-1" @click="goCurrentMonth">{{ lang.nd_day }}</v-btn>
						<v-btn icon small @click="goNextMonth">
							<v-icon small>mdi-chevron-right</v-icon>
						</v-btn>
					</div>
				</div>

				<div class="calendar-grid">
					<div
						v-for="(weekday, weekdayIndex) in weekdayHeaders"
						:key="`weekday_${weekdayIndex}`"
						class="calendar-weekday"
					>
						{{ weekday }}
					</div>

					<div
						v-for="day in calendarDays"
						:key="day.dateKey"
						class="calendar-cell"
						:class="{
							'calendar-cell--outside': !day.isCurrentMonth,
							'calendar-cell--today': day.isToday
						}"
					>
						<div class="calendar-cell-head">
							<div class="calendar-day-title">{{ day.dateNumber }}</div>
						</div>

						<div class="calendar-day-items">
							<v-tooltip
								v-for="item in day.items.slice(0, 3)"
								:key="item.uniqueKey"
								top
							>
								<template v-slot:activator="{ on, attrs }">
									<div
										class="calendar-item-row"
										v-bind="attrs"
										v-on="on"
									>
										<div class="calendar-item-main">
											<div class="calendar-item-title">{{ dayItemLabel(item) }}</div>
											<div class="calendar-item-meta">{{ item.customerName }}</div>
										</div>
										<v-chip x-small dark :color="statusColor(item.statusType)">
											{{ statusLabel(item.statusType) }}
										</v-chip>
									</div>
								</template>
								<div class="calendar-tooltip-wrap">
									<div>{{ lang.nd_mobile }}: {{ item.customerMobile || '-' }}</div>
									<div>{{ lang.nd_plate }}: {{ item.plateNumber || '-' }}</div>
									<div>{{ lang.nd_vehicle }}: {{ item.carModel || '-' }}</div>
								</div>
							</v-tooltip>

							<div v-if="day.items.length > 3" class="calendar-more">
								+{{ day.items.length - 3 }} {{ lang.nd_tasks }}
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
import axios from 'axios';

export default {
	name: 'CalendarDashboardModuleContent',
	props: {
		filters: {
			type: Object,
			required: true
		},
		branchOptions: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			loading: false,
			selectedCalendarType: 'worker_tasks',
			calendarCursor: '',
			deliveryCalendar: [],
			workerCalendar: []
		};
	},
	created() {
		this.syncCalendarCursor();
		this.fetchCalendarData();
	},
	computed: {
		lang: function () {
			if (this.$cookies.get(this.$store.state.LangCooki) != null) {
				if (this.$cookies.get(this.$store.state.LangCooki) == 'en') {
					return this.$store.state.lang.en;
				} else if (this.$cookies.get(this.$store.state.LangCooki) == 'ur') {
					return this.$store.state.lang.ur;
				} else {
					return this.$store.state.lang.ar;
				}
			}
			else if (this.$store.state.deflang == 'en') {
				return this.$store.state.lang.en;
			}
			else if (this.$store.state.deflang == 'ur') {
				return this.$store.state.lang.ur;
			} else {
				return this.$store.state.lang.ar;
			}
		},
		normalizedDateRange() {
			if (!this.filters.startDate && !this.filters.endDate) {
				const end = new Date();
				const start = new Date(end);
				start.setDate(end.getDate() - 30);
				return { start, end };
			}

			const now = new Date();
			const end = this.parseISODate(this.filters.endDate) || now;
			const start =
				this.parseISODate(this.filters.startDate) ||
				new Date(end.getFullYear(), end.getMonth(), 1);
			return start <= end ? { start, end } : { start: end, end: start };
		},
		calendarTypeOptions() {
			return [
				{ value: 'worker_tasks', text: this.lang.nd_worker_tasks },
				{ value: 'delivery_cars', text: this.lang.nd_delivery_cars }
			];
		},
		activeCalendarItems() {
			if (this.selectedCalendarType === 'delivery_cars') {
				return this.deliveryCalendar;
			}
			return this.workerCalendar;
		},
		calendarRenderableItems() {
			const extensionEndDate = new Date();
			extensionEndDate.setHours(0, 0, 0, 0);

			const expanded = [];
			this.activeCalendarItems.forEach((item) => {
				const deliveryDate = this.parseISODate(item.deliveryDate);
				const isNotFinished = item.statusType !== 'closed';

				if (!deliveryDate || !isNotFinished || deliveryDate >= extensionEndDate) {
					expanded.push({
						...item,
						calendarDate: item.deliveryDate
					});
					return;
				}

				let cursor = new Date(deliveryDate);
				while (cursor <= extensionEndDate) {
					const cursorIso = this.toISODate(cursor);
					const deliveryIso = this.toISODate(deliveryDate);
					expanded.push({
						...item,
						uniqueKey: `${item.uniqueKey}_${cursorIso}`,
						calendarDate: cursorIso,
						statusType: cursorIso > deliveryIso ? 'late' : item.statusType
					});
					cursor.setDate(cursor.getDate() + 1);
				}
			});

			return expanded;
		},
		eventsByDate() {
			return this.calendarRenderableItems.reduce((map, item) => {
				const date = item.calendarDate || item.deliveryDate || '';
				if (!date) {
					return map;
				}
				if (!map[date]) {
					map[date] = [];
				}
				map[date].push(item);
				return map;
			}, {});
		},
		calendarLocale() {
			const code = this.lang && this.lang.langname ? String(this.lang.langname).toLowerCase() : 'ar';
			if (code === 'en') return 'en-GB';
			if (code === 'ur') return 'ur-PK';
			return 'ar-SA';
		},
		weekdayHeaders() {
			const base = new Date(2026, 0, 4);
			return Array.from({ length: 7 }, (_, index) => {
				const date = new Date(base);
				date.setDate(base.getDate() + index);
				return date.toLocaleDateString(this.calendarLocale, { weekday: 'short' });
			});
		},
		calendarMonthStart() {
			const baseDate = this.parseISODate(this.calendarCursor) || new Date();
			return new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
		},
		calendarMonthLabel() {
			return this.calendarMonthStart.toLocaleDateString(this.calendarLocale, {
				month: 'long',
				year: 'numeric'
			});
		},
		calendarDays() {
			const monthStart = this.calendarMonthStart;
			const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);

			const gridStart = new Date(monthStart);
			gridStart.setDate(monthStart.getDate() - monthStart.getDay());

			const gridEnd = new Date(monthEnd);
			gridEnd.setDate(monthEnd.getDate() + (6 - monthEnd.getDay()));

			const days = [];
			let cursor = new Date(gridStart);
			while (cursor <= gridEnd) {
				const dateKey = this.toISODate(cursor);
				days.push({
					dateKey,
					dateNumber: cursor.getDate(),
					isCurrentMonth: cursor.getMonth() === monthStart.getMonth(),
					isToday: dateKey === this.toISODate(new Date()),
					items: this.eventsByDate[dateKey] || []
				});
				cursor.setDate(cursor.getDate() + 1);
			}

			return days;
		},
		statusCounts() {
			return this.activeCalendarItems.reduce((sum, item) => {
				if (item.statusType === 'late') {
					sum.late += 1;
				} else if (item.statusType === 'closed') {
					sum.closed += 1;
				} else {
					sum.inProcess += 1;
				}
				return sum;
			}, {
				inProcess: 0,
				late: 0,
				closed: 0
			});
		}
	},
	watch: {
		filters: {
			deep: true,
			handler() {
				this.syncCalendarCursor();
				this.fetchCalendarData();
			}
		}
	},
	methods: {
		dashboardApiPath() {
			const base =
				this.$store &&
				this.$store.state &&
				this.$store.state.SAMCOTEC &&
				this.$store.state.SAMCOTEC.r_path
					? this.$store.state.SAMCOTEC.r_path
					: '';
			if (base && base.indexOf('index.php') !== -1) {
				return base.replace('index.php', 'new-dashboard.php');
			}
			if (base && base.endsWith('/')) {
				return `${base}new-dashboard.php`;
			}
			return 'api/new-dashboard.php';
		},
		normalizeBranchIdsFromFilters() {
			if (this.filters && Array.isArray(this.filters.branches) && this.filters.branches.length) {
				return this.filters.branches;
			}
			return this.branchOptions.map((branch) => branch.id);
		},
		fetchCalendarData() {
			const post = new FormData();
			post.append('module', 'calendar_dashboard');
			post.append('startDate', this.normalizedDateRange.start.toISOString().slice(0, 10));
			post.append('endDate', this.normalizedDateRange.end.toISOString().slice(0, 10));
			post.append('lang', this.lang && this.lang.langname ? this.lang.langname : 'ar');

			const branchIds = this.normalizeBranchIdsFromFilters();
			branchIds.forEach((branchId, index) => {
				post.append(`branchIds[${index}]`, String(branchId));
			});

			this.loading = true;
			return axios.post(this.dashboardApiPath(), post)
				.then((response) => {
					const payload = response && response.data ? response.data : {};
					const data = payload && payload.data ? payload.data : {};
					const apiDelivery = Array.isArray(data.deliveryCalendar) ? data.deliveryCalendar : [];
					const apiWorker = Array.isArray(data.workerCalendar) ? data.workerCalendar : [];

					this.deliveryCalendar = apiDelivery.map((row, index) => ({
						uniqueKey: `delivery_${row.id || row.jobOrderId || index}_${index}`,
						jobOrderId: Number(row.jobOrderId || row.id || 0),
						itemName: '',
						customerName: row.customerName || '-',
						customerMobile: row.customerMobile || '-',
						plateNumber: row.plateNumber || '-',
						carModel: row.carModel || '-',
						employeeName: '',
						deliveryDate: row.deliveryDate || '',
						statusType: row.statusType || 'in_process'
					})).filter((row) => row.jobOrderId > 0);

					this.workerCalendar = apiWorker.map((row, index) => ({
						uniqueKey: `worker_${row.assignmentId || row.workId || index}_${index}`,
						jobOrderId: Number(row.jobOrderId || 0),
						itemName: row.itemName || '',
						customerName: row.customerName || '-',
						customerMobile: row.customerMobile || '-',
						plateNumber: row.plateNumber || '-',
						carModel: row.carModel || '-',
						employeeName: row.employeeName || '-',
						deliveryDate: row.deliveryDate || '',
						statusType: row.statusType || 'in_process'
					})).filter((row) => row.jobOrderId > 0);
				})
				.catch(() => {
					this.deliveryCalendar = [];
					this.workerCalendar = [];
				})
				.finally(() => {
					this.loading = false;
				});
		},
		parseISODate(value) {
			if (!value) return null;
			const [year, month, day] = value.split('-').map(Number);
			if (!year || !month || !day) return null;
			return new Date(year, month - 1, day);
		},
		toISODate(date) {
			if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
				return '';
			}
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		},
		syncCalendarCursor() {
			const start = this.normalizedDateRange.start;
			if (!start) {
				this.calendarCursor = this.toISODate(new Date());
				return;
			}
			this.calendarCursor = this.toISODate(new Date(start.getFullYear(), start.getMonth(), 1));
		},
		goPrevMonth() {
			const current = this.parseISODate(this.calendarCursor) || new Date();
			this.calendarCursor = this.toISODate(new Date(current.getFullYear(), current.getMonth() - 1, 1));
		},
		goNextMonth() {
			const current = this.parseISODate(this.calendarCursor) || new Date();
			this.calendarCursor = this.toISODate(new Date(current.getFullYear(), current.getMonth() + 1, 1));
		},
		goCurrentMonth() {
			const now = new Date();
			this.calendarCursor = this.toISODate(new Date(now.getFullYear(), now.getMonth(), 1));
		},
		formatDateLabel(dateText) {
			const parsed = this.parseISODate(dateText);
			if (!parsed) return dateText || this.lang.nd_no_date;
			return parsed.toLocaleDateString('en-GB', {
				year: 'numeric',
				month: 'short',
				day: '2-digit'
			});
		},
		statusColor(statusType) {
			if (statusType === 'late') return 'error';
			if (statusType === 'closed') return 'success';
			return 'primary';
		},
		statusLabel(statusType) {
			if (statusType === 'late') return this.lang.nd_late;
			if (statusType === 'closed') return this.lang.nd_closed;
			return this.lang.nd_under_process;
		},
		dayItemLabel(item) {
			const carInfo = `${item.plateNumber || '-'} / ${item.carModel || '-'}`;
			if (this.selectedCalendarType === 'worker_tasks') {
				return `${item.employeeName} - ${carInfo}`;
			}
			return `#${item.jobOrderId} - ${carInfo}`;
		}
	}
};
</script>

<style scoped>
.section-card {
	border: 1px solid #e7edf5;
	border-radius: 8px;
	padding: 12px;
	background: #ffffff;
}

.summary-chips-wrap {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	justify-content: flex-end;
}

.calendar-toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 10px;
	gap: 8px;
}


.calendar-title {
	font-weight: 700;
	color: #334155;
	font-size: 0.96rem;
}

.calendar-grid {
	display: grid;
	grid-template-columns: repeat(7, minmax(0, 1fr));
	gap: 8px;
}

.calendar-weekday {
	text-align: center;
	font-size: 0.78rem;
	font-weight: 700;
	color: #64748b;
	padding: 6px 4px;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	background: #f8fafc;
}

.calendar-cell {
	border: 1px solid #e2e8f0;
	border-radius: 10px;
	background: #ffffff;
	min-height: 132px;
	padding: 8px;
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.calendar-cell--outside {
	opacity: 0.55;
	background: #f8fafc;
}

.calendar-cell--today {
	border-color: #1976d2;
	box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.15);
}

.calendar-cell-head {
	display: flex;
	justify-content: flex-end;
	width: 100%;
}

.calendar-day-title {
	font-weight: 700;
	color: #334155;
	font-size: 0.82rem;
}

.calendar-day-items {
	width: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;
	gap: 5px;
}

.calendar-item-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	border: 1px solid #e2e8f0;
	border-radius: 7px;
	padding: 4px 6px;
	gap: 8px;
	background: #f8fafc;
}

.calendar-item-main {
	min-width: 0;
}

.calendar-item-title {
	color: #1f2937;
	font-size: 0.74rem;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.calendar-item-meta {
	color: #64748b;
	font-size: 0.68rem;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.calendar-more {
	font-size: 0.7rem;
	font-weight: 700;
	color: #64748b;
}

.calendar-tooltip-wrap {
	font-size: 0.76rem;
	line-height: 1.4;
	min-width: 170px;
}

@media (max-width: 767px) {
	.summary-chips-wrap {
		justify-content: flex-start;
		margin-top: 8px;
	}

	.calendar-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}
</style>
